var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import styled, { css, keyframes } from 'styled-components';
import { mediaQuery } from '../../config/media';
function toFixed(num, fixed) {
    var re = new RegExp('^-?\\d+(?:\.\\d{0,' + (fixed || -1) + '})?');
    return num.toString().match(re)[0];
}
const calculateBasis = (basis, gap, gapV) => {
    const correctBasis = basis > 12 ? 12 : basis < 1 ? 1 : basis;
    return `
      flex-basis: ${toFixed(100 / correctBasis, 6)}%;
      padding: ${gap}px;
      margin-bottom: ${gapV + 'px'};
      box-sizing: border-box;
      background-origin: content-box;
      background-clip: content-box;
    `;
};
const calculateOffset = (basis, childrens) => {
    const correctBasis = basis > 12 ? 12 : basis < 1 ? 1 : basis;
    const remain = basis - childrens % correctBasis;
    const singleValue = toFixed(100 / correctBasis, 6);
    const remainValue = toFixed(Number(singleValue) * remain, 6);
    return `
      &::after {
        content: "";
        flex-basis: ${remainValue}%;
        background-color: transparent;
      }
    `;
};
const fadeAnimation = keyframes `
 0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;
const transitionAnimationDown = keyframes `
  0% {
     opacity: 0;
     top: -10px;
   }
   30%{
     top: 5px;
   }
   100% {
     opacity: 1;
     top: 0px;
   }
 `;
const transitionAnimationUp = keyframes `
  0% {
     opacity: 0;
     bottom: -10px;
   }
   30%{
     bottom: 5px;
   }
   100% {
     opacity: 1;
     bottom: 0px;
   }
 `;
const transitionAnimationRight = keyframes `
  0% {
     opacity: 0;
     left: -10px;
   }
   30%{
     left: 5px;
   }
   100% {
     opacity: 1;
     left: 0px;
   }
 `;
const transitionAnimationLeft = keyframes `
  0% {
     opacity: 0;
     right: -10px;
   }
   30%{
     right: 5px;
   }
   100% {
     opacity: 1;
     right: 0px;
   }
 `;
const getAnimationDirection = (value) => {
    switch (value) {
        case 'up': return transitionAnimationUp;
        case 'down': return transitionAnimationDown;
        case 'left': return transitionAnimationLeft;
        case 'right': return transitionAnimationRight;
        default: console.error('Insert a correct animation direction: up, down, left, right');
    }
};
const FlexStyle = styled.div `
  display: flex;
  box-sizing: border-box;
  ${props => props.fade && css `
    opacity: 0;
    animation: ${fadeAnimation} ${props => props.animationTime + 'ms'} ease-in-out ${props => props.animationDelay + 'ms'} forwards ;
    `}
  ${props => props.transition && css `
    opacity: 0;
    position: relative;
    animation: ${props => getAnimationDirection(props.animationDirection)} ${props => props.animationTime + 'ms'} ease-in-out ${props => props.animationDelay + 'ms'} forwards ;
    `}
  flex-direction: ${props => (props.column ? 'column' : 'row')};
  flex-wrap: ${props => props.xl || props.xl || props.xl || props.xl || props.xl ? 'wrap' : props.flexWrap};
  justify-content: ${props => props.xl || props.xl || props.xl || props.xl || props.xl ? 'space-between' : props.justifyContent};
  align-items: ${props => props.alignItems};
  align-content: ${props => props.alignContent};
  order: ${props => props.order};
  flex: ${props => props.flex};
  flex-grow: ${props => props.flexGrow};
  flex-shrink: ${props => props.flexShrink};
  align-self: ${props => props.alignSelf};
  padding: ${props => props.padding};
  margin: ${props => props.margin};
  height: ${props => props.height};
  width: ${props => props.width};
  ${props => props.mediaQuery}
  ${props => props.xl && css `
    ${mediaQuery.down({ breakpoint: 'xl' })}{
        .col{
            ${props => calculateBasis(props.xl, props.gap, props.gapV)};
          }
          ${props => props.alignGrid ? calculateOffset(props.xl, props.childrens, props.gap) : null}
      }
    `}
  ${props => props.lg && css `
    ${mediaQuery.down({ breakpoint: 'lg' })}{
        .col{
          ${props => calculateBasis(props.lg, props.gap, props.gapV)};
          }
          ${props => props.alignGrid ? calculateOffset(props.lg, props.childrens, props.gap) : null}
      }
    `}
  ${props => props.md && css `
    ${mediaQuery.down({ breakpoint: 'md' })}{
        .col{
          ${props => calculateBasis(props.md, props.gap, props.gapV)};
          }
          ${props => props.alignGrid ? calculateOffset(props.md, props.childrens, props.gap) : null}
      }
    `}
  ${props => props.sm && css `
    ${mediaQuery.down({ breakpoint: 'sm' })}{
        .col{
          ${props => calculateBasis(props.sm, props.gap, props.gapV)};
          }
          ${props => props.alignGrid ? calculateOffset(props.sm, props.childrens, props.gap) : null}
      }
    `}
  ${props => props.xs && css `
    ${mediaQuery.down({ breakpoint: 'xs' })}{
        .col{
          ${props => calculateBasis(props.xs, props.gap, props.gapV)};
          }
          ${props => props.alignGrid ? calculateOffset(props.xs, props.childrens, props.gap) : null}
      }
    `}
`;
const Box = (_a) => {
    var { column = false, flexWrap, justifyContent, alignItems, alignContent, order, flex, flexGrow, flexShrink, alignSelf, padding, margin, height, width, children, mediaQuery, xl, lg, md, sm, xs, gap, gapV, noMargin, noPadding, alignGrid = true, animationDelay = 0, animationTime = 200, animationDirection = 'up', animationMovement = 10, fade = false, transition = false } = _a, props = __rest(_a, ["column", "flexWrap", "justifyContent", "alignItems", "alignContent", "order", "flex", "flexGrow", "flexShrink", "alignSelf", "padding", "margin", "height", "width", "children", "mediaQuery", "xl", "lg", "md", "sm", "xs", "gap", "gapV", "noMargin", "noPadding", "alignGrid", "animationDelay", "animationTime", "animationDirection", "animationMovement", "fade", "transition"]);
    return (_jsx(FlexStyle, Object.assign({ animationDirection: animationDirection, animationMovement: animationMovement, transition: transition, animationDelay: animationDelay, animationTime: animationTime, fade: fade, column: column, flexWrap: flexWrap, justifyContent: justifyContent, alignItems: alignItems, alignContent: alignContent, order: order, flex: flex, flexGrow: flexGrow, flexShrink: flexShrink, alignSelf: alignSelf, padding: padding, margin: margin, height: height, width: width, mediaQuery: mediaQuery, xl: xl, lg: lg, md: md, sm: sm, xs: xs, gap: gap, gapV: gapV === 0 ? gapV : !gapV ? gap : gapV, childrens: children ? children.length : 0, alignGrid: alignGrid }, props, { children: children })));
};
export default Box;
